import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Country, State, City } from "country-state-city";
import "./VenueForm.css";

const VenueForm = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const user = JSON.parse(sessionStorage.getItem("user"));
  const role = user?.role;
  const uniqueCode = sessionStorage.getItem("uniqueCode");

  // For Services custom dropdown
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const servicesDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    category: "Venues",
    subcategory: "",
    name: "",
    uploadedBy: uniqueCode || "",
    country: "",
    state: "",
    city: "",
    location: "",
    capacity: "",
    price: "",
    email: "",
    images: [],
    video: null,
    services: [],
  });

  const [venues, setVenues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [capacities, setCapacities] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const [imagePreview, setImagePreview] = useState([]);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userMap, setUserMap] = useState({});
  const [vendorServices, setVendorServices] = useState([]);

  const [allCountries] = useState(Country.getAllCountries());
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchVenues();
    fetchUsers();
  }, []);

  // Click outside to close Services dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      const userMapping = {};
      response.data.forEach((user) => {
        userMapping[user.uniqueCode] = user.name || user.role || "Unknown";
      });
      setUserMap(userMapping);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUserMap({ admin123: "Admin" });
    }
  };

  const getUploadedByName = (uniqueCode) => {
    return userMap[uniqueCode] || uniqueCode || "N/A";
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      const processedCategories = res.data.map((cat) => ({
        ...cat,
        subcategory: Array.isArray(cat.subcategory)
          ? cat.subcategory
          : cat.subcategory
          ? [cat.subcategory]
          : [],
      }));
      setCategories(processedCategories);

      const subs = processedCategories
        .filter((cat) => cat.category === "Capacity")
        .flatMap((cat) => cat.subcategory || [])
        .filter((v) => v && `${v}`.trim() !== "");
      const uniqueSubs = [...new Set(subs.map((v) => `${v}`))];
      setCapacities(uniqueSubs);

      const services = processedCategories
        .filter((cat) => cat.category === "Vendors")
        .flatMap((cat) => cat.subcategory || []);
      setVendorServices(services);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchVenues = async () => {
    try {
      const res = await axios.get(`${API_URL}/catagory`);
      const allVenues = res.data.filter((item) => item.category === "Venues");

      if (role === "admin") {
        setVenues(allVenues);
      } else if ((role === "vendor" || role === "venueProvider") && uniqueCode) {
        setVenues(allVenues.filter((v) => v.uploadedBy === uniqueCode));
      } else {
        setVenues([]);
      }
    } catch (err) {
      console.error("Error fetching venues:", err);
    }
  };

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    setFormData((prev) => ({
      ...prev,
      country: countryCode,
      state: "",
      city: "",
    }));
    setStates(countryCode ? State.getStatesOfCountry(countryCode) : []);
    setCities([]);
  };

  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    setFormData((prev) => ({
      ...prev,
      state: stateCode,
      city: "",
    }));
    setCities(stateCode && formData.country ? City.getCitiesOfState(formData.country, stateCode) : []);
  };

  const handleCityChange = (e) => {
    setFormData((prev) => ({ ...prev, city: e.target.value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleService = (service) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const getServicesDisplayText = () => {
    if (formData.services.length === 0) return "Select services...";
    if (formData.services.length === 1) return formData.services[0];
    if (formData.services.length === 2) return `${formData.services[0]}, ${formData.services[1]}`;
    return `${formData.services[0]}, ${formData.services[1]}...`;
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const errors = [];
    const validFiles = [];

    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name} is too large. Maximum size is 10MB.`);
      } else {
        validFiles.push(file);
      }
    });

    setUploadErrors(errors);

    if (validFiles.length > 0) {
      const previews = validFiles.map((file) => URL.createObjectURL(file));
      setImagePreview(previews);
      setFormData((prev) => ({ ...prev, images: validFiles }));
    } else {
      setImagePreview([]);
      setFormData((prev) => ({ ...prev, images: [] }));
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        setUploadErrors([...uploadErrors, `${file.name} is too large. Maximum size is 50MB.`]);
        return;
      }
      setFormData((prev) => ({ ...prev, video: file }));
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (uploadErrors.length > 0) {
      setMessage("Please fix upload errors before submitting.");
      setError(true);
      setIsSubmitting(false);
      return;
    }

    if (formData.images.length === 0 && !editingId) {
      setMessage("Please upload at least one image.");
      setError(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formData.images.forEach((file) => formDataToSend.append("images", file));
      if (formData.video) formDataToSend.append("video", formData.video);

      Object.keys(formData).forEach((key) => {
        if (key !== "images" && key !== "video") {
          formDataToSend.append(
            key,
            Array.isArray(formData[key]) ? JSON.stringify(formData[key]) : formData[key]
          );
        }
      });

      if (editingId) {
        await axios.put(`${API_URL}/catagory/${editingId}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage("Venue updated successfully!");
      } else {
        await axios.post(`${API_URL}/catagory`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage("Venue added successfully!");
      }

      setError(false);
      resetForm();
      fetchVenues();
    } catch (err) {
      console.error("Full error details:", err);
      const errorMessage = err.response?.data?.error || err.message || "Unknown error";
      setMessage(`Error saving venue: ${errorMessage}`);
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: "Venues",
      subcategory: "",
      name: "",
      uploadedBy: uniqueCode || "",
      country: "",
      state: "",
      city: "",
      location: "",
      capacity: "",
      price: "",
      email: "",
      images: [],
      video: null,
      services: [],
    });
    setEditingId(null);
    setImagePreview([]);
    setVideoPreview(null);
    setStates([]);
    setCities([]);
    setUploadErrors([]);
    setIsServicesOpen(false);
  };

  const handleEdit = (venue) => {
    setFormData({
      images: [],
      video: null,
      category: venue.category || "Venues",
      subcategory: venue.subcategory || "",
      uploadedBy: venue.uploadedBy || uniqueCode || "",
      name: venue.name || "",
      country: venue.country || "",
      state: venue.state || "",
      city: venue.city || "",
      location: venue.location || "",
      capacity: venue.capacity != null ? `${venue.capacity}` : "",
      price: venue.price != null ? `${venue.price}` : "",
      email: venue.email || "",
      services: Array.isArray(venue.services)
        ? venue.services
        : venue.services
        ? [venue.services]
        : [],
    });
    setEditingId(venue._id);

    if (venue.images && Array.isArray(venue.images)) {
      setImagePreview(venue.images.map((img) => `${API_URL}/${img}`));
    }

    if (venue.video) {
      setVideoPreview(`${API_URL}/${venue.video}`);
    }

    if (venue.country) {
      setStates(State.getStatesOfCountry(venue.country));
      if (venue.state) {
        setCities(City.getCitiesOfState(venue.country, venue.state));
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this venue?")) return;
    try {
      await axios.delete(`${API_URL}/catagory/${id}`);
      setVenues((prev) => prev.filter((item) => item._id !== id));
    } catch (err) {
      console.error("Error deleting venue:", err);
      alert("Failed to delete. Please try again.");
    }
  };

  return (
    <div className="venue-form-container">
      <h2>{editingId ? "Edit Venue" : "Add New Venue"}</h2>

      {message && (
        <p className={`venue-form-message ${error ? "error" : "success"}`}>
          {message}
        </p>
      )}

      <form onSubmit={handleSubmit} className="common-form">
        <h1 className="mb-4">Venue Form</h1>

        <label>Venue Name:</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        />

        <div className="uploaded-by-field">
          <label>Uploaded By:</label>
          <input
            type="text"
            value={getUploadedByName(formData.uploadedBy)}
            readOnly
            style={{ background: "#f5f5f5", cursor: "not-allowed" }}
          />
        </div>

        <label>Email:</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        />

        {/* Custom Multi-Select for Services Only */}
        <div className="services-dropdown" ref={servicesDropdownRef}>
          <label>Choose Services:</label>
          <div
            className={`services-dropdown-box ${formData.services.length > 0 ? 'has-value' : ''}`}
            onClick={() => !isSubmitting && setIsServicesOpen(!isServicesOpen)}
            style={{
              border: "1px solid #ccc",
              padding: "10px",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              background: "white",
              borderRadius: "4px",
            }}
          >
            <span
              className="services-display"
              title={formData.services.length > 0 ? formData.services.join(', ') : ''}
            >
              {getServicesDisplayText()}
            </span>
            <span style={{ float: "right", fontSize: "18px" }}>
              {isServicesOpen ? "▲" : "▼"}
            </span>
          </div>

          {isServicesOpen && (
            <div
              className="services-dropdown-menu"
              style={{
                border: "1px solid #ccc",
                marginTop: "5px",
                maxHeight: "200px",
                overflowY: "auto",
                background: "white",
                borderRadius: "4px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                position: "absolute",
                zIndex: 1000,
                width: "100%",
              }}
            >
              {vendorServices.map((service, idx) => (
                <div
                  key={idx}
                  className={`service-option ${formData.services.includes(service) ? 'selected' : ''}`}
                  onClick={() => toggleService(service)}
                  style={{
                    padding: "10px",
                    cursor: "pointer",
                    background: formData.services.includes(service) ? "#e6f3ff" : "white",
                  }}
                >
                  <span style={{ marginRight: "8px" }}>
                    {formData.services.includes(service) ? "✓" : ""}
                  </span>
                  {service}
                </div>
              ))}
              <div style={{ textAlign: "right", padding: "8px" }}>
                <button
                  type="button"
                  onClick={() => setIsServicesOpen(false)}
                  style={{
                    padding: "6px 12px",
                    background: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        <label>Address:</label>
        <input
          type="text"
          name="location"
          value={formData.location}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        />

        <label>Country:</label>
        <select
          value={formData.country}
          onChange={handleCountryChange}
          required
          disabled={isSubmitting}
          style={{ width: "100%", padding: "8px" }}
        >
          <option value="">-- Select Country --</option>
          {allCountries.map((c) => (
            <option key={c.isoCode} value={c.isoCode}>
              {c.name}
            </option>
          ))}
        </select>

        {states.length > 0 && (
          <>
            <label>State:</label>
            <select
              value={formData.state}
              onChange={handleStateChange}
              required
              disabled={isSubmitting}
              style={{ width: "100%", padding: "8px" }}
            >
              <option value="">-- Select State --</option>
              {states.map((s) => (
                <option key={s.isoCode} value={s.isoCode}>
                  {s.name}
                </option>
              ))}
            </select>
          </>
        )}

        {cities.length > 0 && (
          <>
            <label>City:</label>
            <select
              value={formData.city}
              onChange={handleCityChange}
              required
              disabled={isSubmitting}
              style={{ width: "100%", padding: "8px" }}
            >
              <option value="">-- Select City --</option>
              {cities.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </>
        )}

        <label>Price:</label>
        <input
          type="number"
          name="price"
          value={formData.price}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        />

        <label>Choose Capacity:</label>
        <select
          name="capacity"
          value={formData.capacity}
          onChange={handleChange}
          disabled={isSubmitting}
          style={{ width: "100%", padding: "8px" }}
        >
          <option value="">-- Select Capacity --</option>
          {capacities.map((cap, idx) => (
            <option key={idx} value={cap}>
              {cap}
            </option>
          ))}
        </select>

        <label>Choose Subcategory:</label>
        <select
          name="subcategory"
          value={formData.subcategory}
          onChange={handleChange}
          disabled={isSubmitting}
          style={{ width: "100%", padding: "8px" }}
        >
          <option value="">-- Select Subcategory --</option>
          {categories
            .find((cat) => cat.category === "Venues")
            ?.subcategory.map((sub, idx) => (
              <option key={idx} value={sub}>
                {sub}
              </option>
            ))}
        </select>

        {/* Rest of form (images, video, submit) remains unchanged */}
        <div className="image-upload-section">
          <label>Venue Images (Maximum 10MB each):</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            disabled={isSubmitting}
          />
          {uploadErrors.length > 0 && (
            <div className="upload-errors">
              {uploadErrors.map((err, i) => (
                <p key={i} className="error-text">{err}</p>
              ))}
            </div>
          )}
          <div className="image-preview-grid">
            {imagePreview.length > 0 ? (
              imagePreview.map((img, i) => (
                <div key={i} className="preview-wrapper">
                  <img src={img} alt={`Preview ${i}`} className="thumbnail-preview" />
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() => {
                      const updatedImages = [...formData.images];
                      updatedImages.splice(i, 1);
                      const updatedPreview = [...imagePreview];
                      updatedPreview.splice(i, 1);
                      setFormData((prev) => ({ ...prev, images: updatedImages }));
                      setImagePreview(updatedPreview);
                    }}
                  >
                    ✖
                  </button>
                </div>
              ))
            ) : (
              <span>No Images Selected</span>
            )}
          </div>
        </div>

        <div className="video-upload-section">
          <label>Venue Video (Maximum 50MB):</label>
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoChange}
            disabled={isSubmitting}
          />
          {videoPreview && (
            <div className="video-preview">
              <video controls className="video-preview-player">
                <source src={videoPreview} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <button
                type="button"
                className="remove-btn"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, video: null }));
                  setVideoPreview(null);
                }}
              >
                ✖
              </button>
            </div>
          )}
        </div>

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Processing..." : editingId ? "Update Venue" : "Add Venue"}
        </button>
      </form>

      {/* Table remains same */}
      <h3>All Venues</h3>
      <table className="venue-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Uploaded By</th>
            <th>Email</th>
            <th>Address</th>
            <th>Capacity</th>
            <th>Price</th>
            <th>Services</th>
            <th>Subcategory</th>
            <th>Images</th>
            <th>Video</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {venues.map((venue) => (
            <tr key={venue._id}>
              <td>{venue.name}</td>
              <td>{getUploadedByName(venue.uploadedBy)}</td>
              <td>{venue.email}</td>
              <td>{venue.location}</td>
              <td>{venue.capacity}</td>
              <td>₹{venue.price}</td>
              <td>{Array.isArray(venue.services) ? venue.services.join(", ") : venue.services || "None"}</td>
              <td>{venue.subcategory || "-"}</td>
              <td>
                {venue.images && venue.images.length > 0 ? (
                  venue.images.slice(0, 3).map((img, index) => (
                    <img
                      key={index}
                      src={`${API_URL}/${img}`}
                      alt={venue.name}
                      style={{ width: "50px", height: "50px", objectFit: "cover", marginRight: "5px" }}
                    />
                  ))
                ) : (
                  "No images"
                )}
              </td>
              <td>
                {venue.video ? (
                  <video controls width="100" height="60">
                    <source src={`${API_URL}/${venue.video}`} type="video/mp4" />
                  </video>
                ) : (
                  "No video"
                )}
              </td>
              <td>
                <button onClick={() => handleEdit(venue)} disabled={isSubmitting}>
                  Edit
                </button>
                <button onClick={() => handleDelete(venue._id)} disabled={isSubmitting}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {venues.length === 0 && (
            <tr>
              <td colSpan="11" style={{ textAlign: "center" }}>
                No venues found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default VenueForm;